import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Eye, EyeOff, ArrowRight, Mail, Lock, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDemoMode } from "@/hooks/useDemoMode";
import DemoBadge from "@/components/DemoBadge";

const ROLES = [
  { key: "principal", email: "principal@schoolos.co.za" },
  { key: "teacher", email: "teacher@schoolos.co.za" },
  { key: "student", email: "student@schoolos.co.za" },
  { key: "parent", email: "parent@schoolos.co.za" },
  { key: "accountant", email: "accountant@schoolos.co.za" },
  { key: "hr", email: "hr@schoolos.co.za" },
  { key: "admin", email: "admin@schoolos.co.za" },
];

export default function Login() {
  const nav = useNavigate();
  const { login, requestOtp } = useAuth();
  const { isDemo } = useDemoMode();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const setRole = (r) => {
    setEmail(r.email);
    setPassword(r.key === "admin" ? "Admin@2026" : "Demo@2026");
    toast.info(`Pre-filled ${r.key} demo credentials`);
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password, otp || undefined);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}`);
      // Owner routes into the owner console; everyone else into their dashboard.
      nav(res.user.role === "owner" ? "/owner" : "/dashboard");
    } else {
      // Auto-open the TOTP field if backend says owner needs a code.
      if (res.error && /TOTP/i.test(res.error)) {
        setOtpMode(true);
        toast.info("Authenticator code required", { description: res.error });
      } else {
        toast.error(res.error);
      }
    }
  };

  const sendOtp = async () => {
    if (!email) return toast.error("Enter your email first");
    setLoading(true);
    const res = await requestOtp(email, "login");
    setLoading(false);
    if (res.ok) {
      setOtpMode(true);
      if (isDemo) {
        toast.success(`OTP sent`, { description: `Demo OTP: ${res.dev_otp}` });
      } else {
        toast.success(`OTP sent to ${email}`, { description: "Check your inbox — it expires in 10 minutes." });
      }
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left: form */}
      <div className="relative flex flex-col p-8 md:p-16 justify-center">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-start justify-between gap-4">
            <BrandLogo />
            <DemoBadge variant="link" />
          </div>

          <h1 className="mt-14 text-5xl md:text-6xl font-bold tracking-tight leading-[0.95]">
            Welcome<br />back.
          </h1>
          <p className="mt-3 text-muted-foreground">Sign in with your school account to continue.</p>
          <p className="mt-2 font-serif-display text-sm text-muted-foreground">Built for South African schools · Grade R–12</p>

          {/* Role quick fill — demo mode only */}
          {isDemo && (
            <div className="mt-8 rounded-2xl border border-dashed border-amber-500/40 bg-amber-500/[0.03] p-4" data-testid="demo-role-panel">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-amber-500 mb-3">
                <Sparkles className="h-3.5 w-3.5" />
                Demo mode · try a role in one click
              </div>
              <div className="grid grid-cols-3 gap-2">
                {ROLES.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setRole(r)}
                    data-testid={`role-quick-${r.key}`}
                    className={cn(
                      "p-3 rounded-xl bg-muted/40 hover:bg-muted transition-colors text-left group"
                    )}
                  >
                    <div className="text-[0.6rem] tracking-[0.18em] uppercase text-primary">
                      {r.key}
                    </div>
                    <div className="text-xs mt-0.5 text-muted-foreground truncate">{r.key === "admin" ? "Admin@2026" : "Demo@2026"}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={doLogin} className="mt-6 space-y-4">
            <div>
              <Label className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="you@school.co.za"
                  className="pl-10 h-12 rounded-2xl"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPw ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-12 rounded-2xl"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {otpMode ? (
              <div>
                <Label className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">2FA — one-time code</Label>
                <div className="relative mt-1.5">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
                  <Input
                    data-testid="login-otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="6-digit code"
                    className="pl-10 h-12 rounded-2xl tracking-widest"
                    inputMode="numeric"
                  />
                </div>
              </div>
            ) : null}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 rounded-2xl text-base font-semibold gap-2"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" />
            </Button>

            <button
              type="button"
              onClick={sendOtp}
              data-testid="request-otp-button"
              className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 pt-1"
            >
              {otpMode ? "Resend one-time code" : "Sign in with 2FA (one-time code)"}
            </button>
          </form>
        </div>
      </div>

      {/* Right: marketing */}
      <div className="hidden lg:block relative bg-card overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute inset-0 bg-radial-fade" />
        <div className="relative h-full flex flex-col justify-end p-16">
          <div className="text-xs tracking-[0.25em] uppercase text-muted-foreground">// School OS™</div>
          <h2 className="mt-3 text-6xl font-bold tracking-tight leading-[0.95]">
            One Platform.<br />
            <span className="text-primary">Every School</span><br />
            Operation.
          </h2>
          <p className="mt-6 max-w-md text-muted-foreground leading-relaxed">
            Principals, teachers, students, parents, accountants and admins — each with a focused workspace, on one operating system built for South African schools.
          </p>
          <p className="mt-6 font-serif-display text-sm text-muted-foreground">Attendance · Fees · Homework · Exams · Timetable · AI · and more.</p>
        </div>
      </div>
    </div>
  );
}
